// Considered: existing inline atlas-empty-state <li> in AtlasTree.tsx — rejected because it is a
// one-line static message with no permission logic, no sectioning, and no add affordances. See spec Reuse Audit.
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { useSpherePermission } from '../../../../hooks/useSpherePermission';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import InlineAddSphereRowMolecule from '../../InlineAddSphereRow/InlineAddSphereRowMolecule';
import GlyphAtom from '../../../atoms/Glyph/GlyphAtom';
import TagAtom from '../../../atoms/Tag/TagAtom';
import Loader from '../../../atoms/loader/loader';
import TemplateCardMolecule from '../../TemplateCard/TemplateCardMolecule';
import { recordRecentType } from './atlasRecentTypes';
import { getCreatableTypes } from '../../../../utils/getCreatableTypes';
import type { Sphere } from 'types/global';
import type { Schema } from 'types/schema';
import type { TemplateApproach } from '../../../../types/templateApproach';
import type { RecentTypeEntry } from './atlasRecentTypes';
import './AtlasEmptyStateMolecule.scss';

const GENERIC_SPHERE_SCHEMA = 'center_sphere';

export interface AtlasEmptyStateMoleculeProps {
  sphere: Sphere;
  /** Organization ID used to scope the client-side recency store. */
  orgId: string;
  availableTypes: Schema[];
  /** True while the available/eligible types are still being fetched — shows a skeleton. */
  typesLoading?: boolean;
  recentTypes: RecentTypeEntry[];
  /** Contextually-suggested deployment templates (ranked upstream). Section hides when empty. */
  suggestedTemplates?: TemplateApproach[];
  onQuickCaptureCapture: (sphere: Sphere) => void;
  onOpenDialog: (tab: 'blank' | 'template', schema?: Schema) => void;
  /** Invoked when a suggested template card is clicked. */
  onSelectTemplate?: (template: TemplateApproach) => void;
  onDeploy: () => void;
  onBrowseMarketplace: () => void;
}

const AtlasEmptyStateMolecule: React.FC<AtlasEmptyStateMoleculeProps> = ({
  sphere,
  orgId,
  availableTypes,
  typesLoading = false,
  recentTypes,
  suggestedTemplates = [],
  onQuickCaptureCapture,
  onOpenDialog,
  onSelectTemplate,
  onDeploy,
  onBrowseMarketplace,
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const { allowed, loading } = useSpherePermission(sphere.id, 'edit');
  const hasLoggedShownRef = useRef(false);

  const sphereName = sphere.properties?.name ?? sphere.label ?? '';
  const parentType = (sphere.properties?.type as string) ?? '';

  // The eligibility evaluator needs the PARENT's own schema in the pool to resolve
  // its node_label and match candidates' assignee_filters. The available-types
  // payload only contains child types, so resolve the parent schema and inject it.
  const { schema: parentSchema } = useSchemaType(parentType || null);

  // Eligibility-filtered types: only types P is allowed to parent (or center, if P is a center).
  const creatable = useMemo(() => {
    if (!parentType) return [];
    const pool = parentSchema ? [...availableTypes, parentSchema] : availableTypes;
    return getCreatableTypes(pool, availableTypes, { type: parentType });
  }, [availableTypes, parentSchema, parentType]);

  // Resolve a template's `templateFor` → schema (for the "Creates a {name}" label + glyph).
  const schemaBySystemName = useMemo(() => {
    const map = new Map<string, Schema>();
    availableTypes.forEach((s) => map.set(s.system_name, s));
    return map;
  }, [availableTypes]);

  // Recency intersection: only entries whose systemName maps to a creatable (eligible) type.
  const recentAvailableTypes = useMemo(() => {
    const recentTypeNames = new Set(recentTypes.map((r) => r.systemName));
    return creatable.filter((s) => recentTypeNames.has(s.system_name));
  }, [recentTypes, creatable]);

  // Capture latest analytics payload in a ref so the effect below can read
  // the values at fire-time without declaring them as deps (the effect is
  // intentionally triggered only when `loading` changes).
  const shownPayloadRef = useRef({
    sphereId: sphere.id,
    canEdit: allowed,
    availableTypeCount: creatable.length,
    hasRecents: recentAvailableTypes.length > 0,
  });
  shownPayloadRef.current = {
    sphereId: sphere.id,
    canEdit: allowed,
    availableTypeCount: creatable.length,
    hasRecents: recentAvailableTypes.length > 0,
  };

  // Fire atlas_empty_state_shown once after permission resolves
  useEffect(() => {
    if (!loading && !hasLoggedShownRef.current) {
      hasLoggedShownRef.current = true;
      logAnalyticsEvent('atlas_empty_state_shown', undefined, shownPayloadRef.current);
    }
  }, [loading, logAnalyticsEvent]);

  const handleQuickCapture = (createdSphere: Sphere) => {
    recordRecentType(orgId, GENERIC_SPHERE_SCHEMA);
    logAnalyticsEvent('atlas_empty_state_action', undefined, {
      sphereId: sphere.id,
      action: 'quick_capture',
    });
    onQuickCaptureCapture(createdSphere);
  };

  const handleTypeChipClick = (schema: Schema) => {
    logAnalyticsEvent('atlas_empty_state_action', undefined, {
      sphereId: sphere.id,
      action: 'start_blank',
      schemaSystemName: schema.system_name,
    });
    onOpenDialog('blank', schema);
  };

  const handleAllTypesClick = () => {
    logAnalyticsEvent('atlas_empty_state_action', undefined, {
      sphereId: sphere.id,
      action: 'all_types',
    });
    onOpenDialog('blank');
  };

  const handleDeployClick = () => {
    logAnalyticsEvent('atlas_empty_state_action', undefined, {
      sphereId: sphere.id,
      action: 'deploy_approach',
    });
    onDeploy();
  };

  const handleBrowseMarketplaceClick = () => {
    logAnalyticsEvent('atlas_empty_state_action', undefined, {
      sphereId: sphere.id,
      action: 'browse_marketplace',
    });
    onBrowseMarketplace();
  };

  const handleSelectTemplate = (template: TemplateApproach) => {
    logAnalyticsEvent('atlas_empty_state_action', undefined, {
      sphereId: sphere.id,
      action: 'suggested_template',
      approachSystemName: template.system_name,
    });
    if (onSelectTemplate) {
      onSelectTemplate(template);
    } else {
      onOpenDialog('template');
    }
  };

  // Compact type chip (glyph + name) — the design's TypeChip, shared by
  // "Start blank" and "Recently created".
  const renderTypeChip = (schema: Schema, testId: string) => (
    <button
      key={schema.system_name}
      type="button"
      className="atlas-empty-state-molecule__chip"
      onClick={() => handleTypeChipClick(schema)}
      data-testid={testId}
    >
      {schema.glyph && (
        <GlyphAtom glyph={schema.glyph} glyphColor={schema.glyph_color ?? undefined} aria-label="" />
      )}
      <span>{schema.name}</span>
    </button>
  );

  return (
    <div className="atlas-empty-state-molecule">
      {/* Hero — renders in all permission states */}
      <div
        className="atlas-empty-state-molecule__hero"
        aria-label={t('atlas.empty.hero.aria_label')}
      >
        <Loader size={120} className="atlas-empty-state-molecule__hero-illustration" />

        <h2 className="atlas-empty-state-molecule__headline">
          <span className="atlas-empty-state-molecule__sphere-name">{sphereName}</span>
          {` ${t('atlas.empty.hero.headline_suffix')}`}
        </h2>
        <p className="atlas-empty-state-molecule__explainer">{t('atlas.empty.hero.explainer')}</p>
      </div>

      {/* Read-only message — shown only when permission resolved as denied */}
      {!loading && !allowed && (
        <p
          className="atlas-empty-state-molecule__readonly-message"
          data-testid="atlas-empty-readonly-message"
        >
          {t('atlas.empty.readonly_message')}
        </p>
      )}

      {/* Affordances — gated on edit permission; fail-closed during loading */}
      {!loading && allowed && (
        <div className="atlas-empty-state-molecule__affordances">
          {/* Quick capture */}
          <div className="atlas-empty-state-molecule__section atlas-empty-state-molecule__section--quick-capture">
            <InlineAddSphereRowMolecule
              parentSphere={sphere}
              onCapture={handleQuickCapture}
              onOpenDialog={(tab) => onOpenDialog(tab)}
              onCancel={() => {
                /* no-op: the row is always visible in the empty state context */
              }}
            />
          </div>

          {/* Build it with the agent — MOCK ONLY (Coming soon). Non-interactive placeholder. */}
          <div
            className="atlas-empty-state-molecule__agent-card"
            aria-disabled="true"
            data-testid="atlas-empty-agent-card"
          >
            <span className="atlas-empty-state-molecule__agent-icon">
              <GlyphAtom glyph="lucide:bot" aria-label="" />
            </span>
            <div className="atlas-empty-state-molecule__agent-content">
              <div className="atlas-empty-state-molecule__agent-header">
                <span className="atlas-empty-state-molecule__agent-title">
                  {t('atlas.empty.agent.title')}
                </span>
                <TagAtom
                  label={t('atlas.empty.agent.badge')}
                  tone="info"
                  shape="pill"
                  size="small"
                  icon="pi-clock"
                />
              </div>
              <p className="atlas-empty-state-molecule__agent-description">
                {t('atlas.empty.agent.description')}
              </p>
            </div>
          </div>

          {/* Suggested for this department — contextual deployment templates */}
          {suggestedTemplates.length > 0 && (
            <section
              className="atlas-empty-state-molecule__section"
              data-testid="atlas-empty-suggested-section"
            >
              <div className="atlas-empty-state-molecule__section-head">
                <i className="pi pi-sparkles atlas-empty-state-molecule__section-icon" aria-hidden="true" />
                <span className="atlas-empty-state-molecule__section-title">
                  {t('atlas.empty.suggested.title')}
                </span>
                <span className="atlas-empty-state-molecule__section-hint">
                  {t('atlas.empty.suggested.hint')}
                </span>
              </div>
              <div className="atlas-empty-state-molecule__template-grid">
                {suggestedTemplates.slice(0, 3).map((tpl) => {
                  const schema = tpl.templateFor
                    ? schemaBySystemName.get(tpl.templateFor)
                    : undefined;
                  return (
                    <TemplateCardMolecule
                      key={tpl.system_name}
                      template={tpl}
                      schemaName={schema?.name}
                      schemaGlyph={schema?.glyph}
                      onClick={handleSelectTemplate}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {/* Recently created — shown only when recency ∩ availableTypes is non-empty */}
          {recentAvailableTypes.length > 0 && (
            <section
              className="atlas-empty-state-molecule__section"
              data-testid="atlas-empty-recent-section"
            >
              <div className="atlas-empty-state-molecule__section-head">
                <i className="pi pi-history atlas-empty-state-molecule__section-icon" aria-hidden="true" />
                <span className="atlas-empty-state-molecule__section-title">
                  {t('atlas.empty.recent.title')}
                </span>
                <span className="atlas-empty-state-molecule__section-hint">
                  {t('atlas.empty.recent.hint')}
                </span>
              </div>
              <div className="atlas-empty-state-molecule__chips">
                {recentAvailableTypes.map((schema) =>
                  renderTypeChip(schema, `atlas-empty-recent-chip-${schema.system_name}`),
                )}
              </div>
            </section>
          )}

          {/* Start blank + Bring in methodology — two-column */}
          <div className="atlas-empty-state-molecule__split">
            <section
              className="atlas-empty-state-molecule__section"
              data-testid="atlas-empty-start-blank-section"
            >
              <div className="atlas-empty-state-molecule__section-head">
                <i className="pi pi-clone atlas-empty-state-molecule__section-icon" aria-hidden="true" />
                <span className="atlas-empty-state-molecule__section-title">
                  {t('atlas.empty.start_blank.title')}
                </span>
                <span className="atlas-empty-state-molecule__section-hint">
                  {t('atlas.empty.start_blank.hint')}
                </span>
              </div>
              {typesLoading ? (
                <div
                  className="atlas-empty-state-molecule__chips"
                  data-testid="atlas-empty-types-loading"
                  aria-busy="true"
                  aria-label={t('atlas.empty.start_blank.loading')}
                >
                  {['a', 'b', 'c', 'd', 'e'].map((k) => (
                    <span
                      key={`chip-skeleton-${k}`}
                      className="atlas-empty-state-molecule__chip-skeleton"
                      aria-hidden="true"
                    />
                  ))}
                </div>
              ) : (
                <div className="atlas-empty-state-molecule__chips">
                  {creatable.map((schema) =>
                    renderTypeChip(schema, `atlas-empty-type-chip-${schema.system_name}`),
                  )}
                  <button
                    type="button"
                    className="atlas-empty-state-molecule__all-types-btn"
                    onClick={handleAllTypesClick}
                    data-testid="atlas-empty-all-types-btn"
                  >
                    <i className="pi pi-th-large" aria-hidden="true" />
                    {t('atlas.empty.start_blank.all_types')}
                  </button>
                </div>
              )}
            </section>

            <section
              className="atlas-empty-state-molecule__section"
              data-testid="atlas-empty-deploy-section"
            >
              <div className="atlas-empty-state-molecule__section-head">
                <i className="pi pi-sitemap atlas-empty-state-molecule__section-icon" aria-hidden="true" />
                <span className="atlas-empty-state-molecule__section-title">
                  {t('atlas.empty.methodology.title')}
                </span>
                <span className="atlas-empty-state-molecule__section-hint">
                  {t('atlas.empty.methodology.hint')}
                </span>
              </div>
              <button
                type="button"
                className="atlas-empty-state-molecule__deploy-btn"
                onClick={handleDeployClick}
                data-testid="atlas-empty-deploy-btn"
              >
                <span className="atlas-empty-state-molecule__deploy-icon">
                  <i className="pi pi-sitemap" aria-hidden="true" />
                </span>
                <span className="atlas-empty-state-molecule__deploy-text">
                  <span className="atlas-empty-state-molecule__deploy-title">
                    {t('atlas.empty.deploy.title')}
                  </span>
                  <span className="atlas-empty-state-molecule__deploy-subtitle">
                    {t('atlas.empty.deploy.subtitle')}
                  </span>
                </span>
                <i className="pi pi-arrow-right" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="atlas-empty-state-molecule__marketplace-btn"
                onClick={handleBrowseMarketplaceClick}
                data-testid="atlas-empty-marketplace-btn"
              >
                <i className="pi pi-shopping-bag" aria-hidden="true" />
                {t('atlas.empty.deploy.browse_marketplace')}
              </button>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default AtlasEmptyStateMolecule;
